import { Group } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  Missing,
  DateCell,
  getCheckboxColumnDefs,
  TextCell,
  BoolCell,
} from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CertificateStatus } from "@tfg/types/constants/certificates.constants"
import { CertificateCategory } from "@tfg/types/schemas/certificates"

import { ProductionSiteCell } from "~/components/cells/ProductionSiteCell.tsx"
import {
  CertificateByOrganizationCertificateRequestDto,
  CertificateByOrganizationDto,
} from "~/gen/api"
import { CertificateRequestsSummaryBadge } from "~/pages/Brand/Certificates/components/CertificateRequestsSummaryBadge"
import { getScoreTranslation } from "~/pages/Brand/Certificates/type"

const columnHelper = createColumnHelper<CertificateByOrganizationDto>()

export const useColumns = () => {
  const { t } = useTranslation("certificates")

  return useMemo<ColumnDef<CertificateByOrganizationDto, any>[]>(
    () => [
      getCheckboxColumnDefs<CertificateByOrganizationDto>(),
      // DO NOT REMOVE : Needed here for the below dynamic category translations
      // t("lists.columns.category.TRANSACTION")
      // t("lists.columns.category.SCOPE")
      // t("lists.columns.category.OTHER_INITIATIVES")
      // t("lists.columns.category.UNKNOWN")
      columnHelper.accessor("type.category", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.category.header")}
            tooltip={`${t("lists.columns.category.header")} : ${t("lists.columns.category.tooltip")}`}
          />
        ),
        cell: (info) => {
          const category: CertificateCategory = info.getValue()
          return (
            <TextCell value={`${t(`lists.columns.category.${category}`)}`} />
          )
        },
        enableSorting: false,
        enablePinning: true,
      }),
      columnHelper.accessor("type.name", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.type.header")}
            tooltip={`${t("lists.columns.type.header")} : ${t("lists.columns.type.tooltip")}`}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
        enableSorting: false,
        enablePinning: true,
      }),
      columnHelper.accessor("name", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.name.header")}
            tooltip={`${t("lists.columns.name.header")} : ${t("lists.columns.name.tooltip")}`}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
        enableSorting: false,
        enablePinning: true,
      }),
      columnHelper.accessor("reference", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.reference.header")}
            tooltip={`${t("lists.columns.reference.header")} : ${t("lists.columns.reference.tooltip")}`}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor("score", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.score.header")}
            tooltip={`${t("lists.columns.score.header")} : ${t("lists.columns.score.tooltip")}`}
          />
        ),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <Missing />
          return <TextCell value={getScoreTranslation(value, t)} />
        },
        enableSorting: false,
      }),
      columnHelper.accessor("productionSite.name", {
        id: "company",
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.productionSite.header")}
            tooltip={`${t("lists.columns.productionSite.header")} : ${t("lists.columns.productionSite.tooltip")}`}
          />
        ),
        cell: ({ row: { original: certificate } }) => {
          return (
            <ProductionSiteCell
              productionSite={certificate.productionSite ?? undefined}
            />
          )
        },
        enableSorting: false,
      }),

      columnHelper.accessor("issueDate", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.issueDate.header")}
            tooltip={`${t("lists.columns.issueDate.header")} : ${t("lists.columns.issueDate.tooltip")}`}
          />
        ),
        cell: (info) => <DateCell date={info.getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor("expiryDate", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.expiryDate.header")}
            tooltip={`${t("lists.columns.expiryDate.header")} : ${t("lists.columns.expiryDate.tooltip")}`}
          />
        ),
        cell: (info) => <DateCell date={info.getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor("businessPartner", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.businessPartner.header")}
            tooltip={`${t("lists.columns.businessPartner.header")} : ${t("lists.columns.businessPartner.tooltip")}`}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
        enableSorting: false,
      }),

      columnHelper.accessor("createdAt", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.createdAt.header")}
            tooltip={`${t("lists.columns.createdAt.header")} : ${t("lists.columns.createdAt.tooltip")}`}
          />
        ),
        cell: (info) => <DateCell date={info.getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor(
        ({ purchaseOrders }) => purchaseOrders.product.join(", "),
        {
          id: "purchaseOrders-product",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("lists.columns.purchaseOrders.product.header")}
              tooltip={t("lists.columns.purchaseOrders.product.tooltip")}
            />
          ),
          cell: ({ row }) => {
            const pos = row.original.purchaseOrders.product
            if (!pos.length) return <Missing />
            return <TextCell value={pos.join(", ")} />
          },
          enableSorting: false,
        }
      ),
      columnHelper.accessor(
        ({ purchaseOrders }) => purchaseOrders.component.join(", "),
        {
          id: "purchaseOrders-component",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("lists.columns.purchaseOrders.component.header")}
              tooltip={t("lists.columns.purchaseOrders.component.tooltip")}
            />
          ),
          cell: ({ row }) => {
            const pos = row.original.purchaseOrders.component
            if (!pos.length) return <Missing />
            return <TextCell value={pos.join(", ")} />
          },
          enableSorting: false,
        }
      ),
      columnHelper.accessor(
        ({ purchaseOrders }) => purchaseOrders.material.join(", "),
        {
          id: "purchaseOrders-material",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("lists.columns.purchaseOrders.material.header")}
              tooltip={t("lists.columns.purchaseOrders.material.tooltip")}
            />
          ),
          cell: ({ row }) => {
            const pos = row.original.purchaseOrders.material
            if (!pos.length) return <Missing />
            return <TextCell value={pos.join(", ")} />
          },
          enableSorting: false,
        }
      ),
      columnHelper.accessor("status", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.status.header")}
            tooltip={`${t("lists.columns.status.header")} : ${t("lists.columns.status.tooltip")}`}
          />
        ),
        size: 100,
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue()

          return (
            <Group gap={4} align="center" justify="center">
              <BoolCell
                value={value === CertificateStatus.PROCESSED}
                withText={false}
              />
            </Group>
          )
        },
      }),
      columnHelper.accessor("certificateRequests", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("lists.columns.certificateRequests.header")}
            tooltip={t("lists.columns.certificateRequests.tooltip")}
          />
        ),
        size: 170,
        maxSize: 170,
        enableSorting: false,
        cell: (info) => {
          const requests: CertificateByOrganizationCertificateRequestDto[] =
            info.getValue()

          return <CertificateRequestsSummaryBadge requests={requests} />
        },
      }),
    ],
    [t]
  )
}
